import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaQrcode, FaCopy, FaExternalLinkAlt } from 'react-icons/fa';
import { SiEthereum, SiSolana, SiPolygon } from 'react-icons/si';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

const CHAIN_ICONS = {
  ethereum: SiEthereum,
  polygon: SiPolygon,
  solana: SiSolana,
};

const CHAIN_COLORS = {
  ethereum: '#627EEA',
  polygon: '#8247E5',
  tron: '#EB0029',
  solana: '#14F195',
};

export default function TokenDetailDrawer({ token, addresses, onClose, onDeposit }) {
  const [showQR, setShowQR] = useState(null);

  if (!token) return null;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const chainAddresses = addresses.filter(addr => 
    Object.keys(token.chains || {}).includes(addr.chain)
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-end md:items-center justify-center p-4"
        onClick={onClose}
        data-testid="token-detail-drawer"
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#0f172a] border border-[#1e293b] w-full max-w-3xl max-h-[85vh] overflow-y-auto"
        >
          {/* Header */}
          <div 
            className="sticky top-0 z-10 bg-[#0f172a] border-b border-[#1e293b] p-6 flex items-center justify-between"
            style={{ borderBottomColor: token.color + '40' }}
          >
            <div className="flex items-center">
              <div 
                className="w-12 h-12 flex items-center justify-center mr-4"
                style={{ backgroundColor: token.color + '20' }}
              >
                <span className="text-2xl font-bold" style={{ color: token.color }}>
                  {token.symbol[0]}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                  {token.symbol}
                </h2>
                <p className="text-sm text-[#94a3b8]">{token.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-[#94a3b8] hover:text-white transition-colors p-2"
              data-testid="close-drawer-button"
            >
              <FaTimes className="text-2xl" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Balance Summary */}
            <div className="bg-[#020617] border border-[#1e293b] p-6">
              <p className="text-sm text-[#94a3b8] uppercase tracking-wider mb-2">Total Balance</p>
              <p className="text-4xl font-extrabold text-white mb-2 mono" style={{ fontFamily: 'Manrope' }}>
                {token.balance.toFixed(6)} {token.symbol}
              </p>
              <p className="text-lg text-[#94a3b8]">
                ≈ ${token.balance_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>

            {/* Per-Chain Breakdown */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                Balance by Chain
              </h3>
              <div className="space-y-3">
                {Object.entries(token.chains || {}).map(([chain, balance]) => {
                  const Icon = CHAIN_ICONS[chain];
                  const color = CHAIN_COLORS[chain];
                  const address = chainAddresses.find(a => a.chain === chain);

                  return (
                    <div
                      key={chain}
                      className="bg-[#020617] border border-[#1e293b] p-4 hover:border-[#ccff00]/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          {Icon && <Icon className="text-2xl mr-3" style={{ color }} />}
                          <div>
                            <p className="text-white font-bold capitalize" style={{ fontFamily: 'Manrope' }}>
                              {chain}
                            </p>
                            <p className="text-sm text-[#94a3b8]">
                              {balance.toFixed(6)} {token.symbol}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          {address && (
                            <>
                              <button
                                onClick={() => setShowQR(showQR === chain ? null : chain)}
                                className="bg-[#1e293b] text-white p-2 hover:bg-[#334155] transition-colors"
                                data-testid={`qr-button-${chain}`}
                              >
                                <FaQrcode />
                              </button>
                              <button
                                onClick={() => onDeposit(address)}
                                className="bg-[#ccff00] text-black px-4 py-2 hover:bg-[#b3e600] transition-colors font-bold text-sm"
                                data-testid={`deposit-button-${chain}`}
                              >
                                Deposit
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {address && (
                        <>
                          <div className="bg-[#0f172a] border border-[#1e293b] p-3 flex items-center justify-between">
                            <p className="mono text-xs text-[#94a3b8] truncate mr-2">
                              {address.address}
                            </p>
                            <button
                              onClick={() => copyToClipboard(address.address)}
                              className="text-[#94a3b8] hover:text-[#ccff00] transition-colors"
                            >
                              <FaCopy />
                            </button>
                          </div>

                          {/* QR Code */}
                          <AnimatePresence>
                            {showQR === chain && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="flex justify-center py-4 bg-[#0f172a] mt-3"
                              >
                                <QRCodeSVG 
                                  value={address.address} 
                                  size={200} 
                                  bgColor="#0f172a" 
                                  fgColor={color} 
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}